import React, { useContext, useRef, useEffect, useState } from "react";
import { X } from "lucide-react";
import "./notification-premium.css";
import { net } from "../../database";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import useDBO from "../content/hooks/useDBO";
import Loading from "../content/Loading";
import { UserContext } from "../../Context";

/**
 * DrawNotification - Renders a single notification item
 * Uses lucide-react X icon instead of MUI CloseIcon.
 *
 * @param {object} props Component props
 * @param {object} props.notification Notification data
 * @param {object} props.notifications Notifications collection
 * @author Forge
 * @date 2026-04-07
 * @version 3.0
 */
function DrawNotification(props) {
  const { user } = useContext(UserContext);
  let { notification, notifications } = props;
  let { title, message, notes, students, classes, customers, leaveRequests, timeStamp } =
    notification;

  const dismissNotification = (id) => {
    net
      .post(`/user/${user._id}/dismissnotification`, { id })
      .then((notification_id) => {
        notifications.remove(id);
      });
  };

  var titleLinks = "";
  try {
    titleLinks = title.split(" ").map((item, i) => {
      if (item === "<s0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/student/${students[0]._id}`}
          >
            {" "}
            {students[0].name}
          </Link>
        );
      }
      if (item === "<htc0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/class/${classes[0]._id}`}
          >
            {" "}
            {classes[0].name}
          </Link>
        );
      }
      if (item === "<htc1>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/class/${classes[1]._id}`}
          >
            {" "}
            {classes[1].name}
          </Link>
        );
      }
      if (item === "<c0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/customer/${customers[0]._id}`}
          >
            {" "}
            {customers[0].name}
          </Link>
        );
      }
      if (item === "<lr0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to="/manage/leave-approvals"
          >
            {" "}
            {leaveRequests?.[0]?.name || "leave request"}
          </Link>
        );
      }
      return <span key={`${item}${i}`}> {item}</span>;
    });
  } catch (error) {
    console.error(error);
  }

  var messageLinks = "";
  try {
    messageLinks = message.split(" ").map((item, i) => {
      if (item === "<s0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/student/${students[0]}`}
          >
            {" "}
            {students[0].name}
          </Link>
        );
      }
      if (item === "<htc0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/class/${classes[0]._id}`}
          >
            {" "}
            {classes[0].name}
          </Link>
        );
      }
      if (item === "<htc1>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/class/${classes[1]._id}`}
          >
            {" "}
            {classes[1].name}
          </Link>
        );
      }
      if (item === "<c0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to={`/customer/${customers[0]._id}`}
          >
            {" "}
            {customers[0].name}
          </Link>
        );
      }
      if (item === "<lr0>") {
        return (
          <Link
            key={`${item}${i}`}
            className="notification-link"
            to="/manage/leave-approvals"
          >
            {" "}
            {leaveRequests?.[0]?.name || "leave request"}
          </Link>
        );
      }
      return <span key={`${item}${i}`}> {item}</span>;
    });
  } catch (error) {
    console.log(error);
  }

  return (
    <li className="notification-item">
      <div className="notification-item-content">
        <div className="notification-item-main">
          <div className="notification-title">{titleLinks}</div>
          <div className="notification-message">{messageLinks}</div>
          <div className="notification-notes">{notes}</div>
        </div>
        <div className="notification-item-right">
          <div className="notification-timestamp">
            {format(new Date(timeStamp), "h:mm aaa dd/MM/yy")}
          </div>
          <button
            className="notification-dismiss-button"
            onClick={() => dismissNotification(notification._id)}
            aria-label="Dismiss notification"
            type="button"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Notification dropdown component.
 * Custom positioning instead of MUI Popper.
 * Custom click-away instead of MUI ClickAwayListener.
 *
 * @param {object} props Component props
 * @param {object} props.NanchorEl Anchor element for positioning
 * @param {boolean} props.isNMenuOpen Whether the notification dropdown is open
 * @param {function} props.handleNotificationsClose Close handler
 * @author Forge
 * @date 2026-04-07
 * @version 3.0
 */
export default function Notification(props) {
  const { user } = useContext(UserContext);
  const notifications = useDBO("/notifications", 60);
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate position from anchor element
  useEffect(() => {
    if (props.NanchorEl && props.isNMenuOpen) {
      const rect = props.NanchorEl.getBoundingClientRect();
      const top = rect.bottom + 8;
      const right = window.innerWidth - rect.right;
      setPosition({ top, right });
    }
  }, [props.NanchorEl, props.isNMenuOpen]);

  // Click away listener
  useEffect(() => {
    if (!props.isNMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        props.NanchorEl &&
        !props.NanchorEl.contains(event.target)
      ) {
        props.handleNotificationsClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        props.handleNotificationsClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [props.isNMenuOpen, props.handleNotificationsClose, props.NanchorEl]);

  if (notifications.loading) return <Loading />;
  if (notifications.error) return <></>;

  const clearNotifications = () => {
    net.post(`/user/${user._id}`, { unreadNotifications: [] }).then((res) => {
      notifications.set([]);
    });
  };

  if (!props.isNMenuOpen) return null;

  if (notifications.get.length === 0) {
    return (
      <div
        ref={dropdownRef}
        className="notification-dropdown"
        style={{ top: position.top, right: position.right }}
      >
        <div className="notification-header">
          <h3 className="notification-header-title">Notifications</h3>
          <button
            className="notification-clear-button"
            onClick={() => clearNotifications()}
            type="button"
          >
            Clear all
          </button>
        </div>
        <div className="notification-empty">
          <div className="notification-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <p className="notification-empty-text">No notifications</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="notification-dropdown"
      style={{ top: position.top, right: position.right }}
    >
      <div className="notification-header">
        <h3 className="notification-header-title">Notifications</h3>
        <button
          className="notification-clear-button"
          onClick={() => clearNotifications()}
          type="button"
        >
          Clear all
        </button>
      </div>
      <ul className="notification-list">
        {notifications.get.map((notification) => (
          <DrawNotification
            key={notification._id}
            notification={notification}
            notifications={notifications}
          />
        ))}
      </ul>
    </div>
  );
}
